import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Divider } from "@chakra-ui/react";
import DOMPurify from "dompurify";
import { Loader } from "components";
import { ApiRequestStatusEnum } from "enums/api-request-status.enum";
import { AccountGrsPendingLeadsStore } from "store/UserAccounts/UserAccount/AccountGrsPendingLeads.store";
import { TGrsPendingLeadDetail } from "types/grs-pending-lead.type";
import { TAutomatedReferralTermsData } from "types/automated-referrals.type";
import { useIsMountedRef } from "utils/react-hooks/useIsMountedRef.hook";
import { GrsPendingLeadDetailsPanel } from "./GrsPendingLeadDetailsPanel";
import { GrsPendingLeadLoadError } from "./GrsPendingLeadLoadError";

const GRS_TERMS_HEADING = "Terms and Conditions";
const GRS_TERMS_ERROR_ID = "grs-pending-lead-terms-error";
const IN_PAGE_OR_MAIL_OR_PHONE_HREF = /^(#|mailto:|tel:)/i;

type TSanitisedTerms = { id: number; html: string };

const hasVisibleText = (html: string) =>
  !!new DOMParser().parseFromString(html, "text/html").body.textContent?.trim();

const sanitiseTerms = ({
  id,
  content,
}: TAutomatedReferralTermsData): TSanitisedTerms | null => {
  const html = DOMPurify.sanitize(content, {
    FORBID_TAGS: ["style", "form"],
    FORBID_ATTR: ["style"],
  });

  return hasVisibleText(html) ? { id, html } : null;
};

const openLinksInNewTab = (container: HTMLElement | null) => {
  container?.querySelectorAll("a").forEach(anchor => {
    if (IN_PAGE_OR_MAIL_OR_PHONE_HREF.test(anchor.getAttribute("href") ?? "")) {
      return;
    }

    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
  });
};

interface IGrsPendingLeadTermsStepProps {
  detail: TGrsPendingLeadDetail;
  accountGrsPendingLeadsStore: AccountGrsPendingLeadsStore;
  isSubmitting: boolean;
  onAccept: (termsId: number) => Promise<boolean>;
  onCancel: () => void;
}

export const GrsPendingLeadTermsStep: FC<IGrsPendingLeadTermsStepProps> = ({
  detail,
  accountGrsPendingLeadsStore,
  isSubmitting,
  onAccept,
  onCancel,
}) => {
  const [terms, setTerms] = useState<TSanitisedTerms | null>(null);
  const [termsStatus, setTermsStatus] = useState(ApiRequestStatusEnum.NONE);
  const isMountedRef = useIsMountedRef();
  const termsRegionRef = useRef<HTMLDivElement>(null);
  const acceptButtonRef = useRef<HTMLButtonElement>(null);

  const loadTerms = useCallback(async () => {
    setTermsStatus(ApiRequestStatusEnum.PENDING);

    try {
      const sanitisedTerms = sanitiseTerms(
        await accountGrsPendingLeadsStore.fetchTerms()
      );
      if (!isMountedRef.current) return;
      setTerms(sanitisedTerms);
      setTermsStatus(
        sanitisedTerms
          ? ApiRequestStatusEnum.SUCCESS
          : ApiRequestStatusEnum.ERROR
      );
    } catch (error) {
      if (!isMountedRef.current) return;
      setTermsStatus(ApiRequestStatusEnum.ERROR);
    }
  }, [accountGrsPendingLeadsStore, isMountedRef]);

  useEffect(() => {
    loadTerms();
  }, [loadTerms]);

  const retryTerms = useCallback(async () => {
    await loadTerms();
    termsRegionRef.current?.focus();
  }, [loadTerms]);

  const hasTermsError = termsStatus === ApiRequestStatusEnum.ERROR;
  const canAccept =
    !!terms && termsStatus === ApiRequestStatusEnum.SUCCESS && !isSubmitting;

  const handleAccept = useCallback(async () => {
    if (!terms) return;
    const succeeded = await onAccept(terms.id);
    if (!succeeded) acceptButtonRef.current?.focus();
  }, [onAccept, terms]);

  return (
    <Box>
      <Box mb={5} color={"leadpro.700"}>
        Please review the lead details and Terms and Conditions below before
        accepting this lead into your Leadpro dashboard.
      </Box>
      <GrsPendingLeadDetailsPanel detail={detail} />
      <Divider mb={4} />
      <Box fontWeight={"bold"} color={"leadpro.700"} mb={2}>
        {GRS_TERMS_HEADING}
      </Box>
      <Box
        ref={termsRegionRef}
        tabIndex={0}
        role={"region"}
        aria-label={GRS_TERMS_HEADING}
        height={"480px"}
        overflowY={"auto"}
        border={"1px solid"}
        borderColor={"gray.200"}
        rounded={"md"}
        p={4}
        mb={4}
        position={"relative"}
      >
        {termsStatus === ApiRequestStatusEnum.PENDING && <Loader />}
        {hasTermsError && (
          <GrsPendingLeadLoadError
            id={GRS_TERMS_ERROR_ID}
            message={"We couldn't load the terms and conditions."}
            onRetry={retryTerms}
          />
        )}
        {termsStatus === ApiRequestStatusEnum.SUCCESS && !!terms && (
          <Box
            ref={openLinksInNewTab}
            sx={{ a: { textDecoration: "underline" } }}
            dangerouslySetInnerHTML={{ __html: terms.html }}
          />
        )}
      </Box>
      <Box display={"flex"} justifyContent={"flex-end"}>
        <Button
          mr={3}
          variant={"outline"}
          onClick={onCancel}
          isDisabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          ref={acceptButtonRef}
          colorScheme={"blue"}
          onClick={handleAccept}
          isDisabled={!canAccept}
          isLoading={isSubmitting}
          aria-describedby={hasTermsError ? GRS_TERMS_ERROR_ID : undefined}
        >
          Accept
        </Button>
      </Box>
    </Box>
  );
};
